import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Card — Generated from Figma Nimbus v3 Design System
 * @figma-node 23358:6230
 * @figma-url https://www.figma.com/design/euN3FdxtNlCC8GiKC66YmG/Nimbus-v3--TBD-?node-id=23358:6230
 *
 * Variants: highlighted (true/false) x padding (none/sm/md/lg) = 8 variants
 *
 * All colors extracted from Figma API — not guessed.
 *
 * Base: bg-nimbus-background, border nimbus-border, rounded-xl, shadow
 * Highlighted: green gradient border (#349920 → nimbus-border → nimbus-border → #349920, 135deg)
 * Padding: none=p-0, sm=p-3, md=p-6, lg=p-12
 */

const cardVariants = cva(
  'flex flex-col gap-6 bg-nimbus-background text-nimbus-foreground shadow-nimbus-md relative',
  {
    variants: {
      highlighted: {
        true: 'border border-transparent [background:linear-gradient(var(--nimbus-background),var(--nimbus-background))_padding-box,linear-gradient(135deg,#349920_0%,var(--nimbus-border)_25%,var(--nimbus-border)_75%,#349920_100%)_border-box]',
        false: 'border border-nimbus-border',
      },
      padding: {
        none: 'p-0 rounded-[16px]',
        sm: 'p-3 rounded-nimbus-md',
        md: 'p-6 rounded-[16px]',
        lg: 'p-12 rounded-[2rem]',
      },
    },
    defaultVariants: {
      highlighted: false,
      padding: 'md',
    },
  }
);

interface CardProps
  extends React.ComponentProps<'div'>,
    VariantProps<typeof cardVariants> {}

function Card({ className, highlighted, padding, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-highlighted={highlighted || undefined}
      className={cn(cardVariants({ highlighted, padding }), className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 has-data-[slot=card-action]:grid-cols-[1fr_auto]',
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'h3'>) {
  return (
    <h3
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-nimbus-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn(className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  cardVariants,
};
export type { CardProps };
